import React from 'react';
import { styled } from '@mui/material/styles';
import { Container, Toolbar, List, Typography, Divider, IconButton, CssBaseline, Box, Drawer as MuiDrawer, useTheme } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon} from '@mui/icons-material';
import { useLocation, useOutlet } from 'react-router-dom';
import { mainListItems, secondaryListItems } from '../../data/dashboard-menu';
import UserMenu from '../../components/user-menu';
import Copyright from '../../components/copyright';
import { CustomScrollbarStyles } from '../../themes/styled-components';
import logo from './../../assets/images/hce_logo_white.png';
import { useAuth } from '../../context/auth-provider';
import { trackTelemetryEvent, getUserGroupsFromIdToken } from '../../utils/telemetry';

/* eslint-disable-next-line */
const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

export default function Dashboard() {

  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  const outlet = useOutlet();  
  const [open, setOpen] = React.useState(false);
  
  const toggleDrawer = () => setOpen(!open);

  React.useEffect(() => {
    if (!user) return;
    trackTelemetryEvent({
      userSub: user.userId,
      userEmail: user.email,
      userGroups: getUserGroupsFromIdToken(user.idToken),
      authToken: user.idToken,
      actionType: 'page_view',
      actionTarget: location.pathname || '/',
      status: 'success',
    });
  }, [location.pathname, user]);

  return (
    <Box sx={{ display: 'flex' }}>

      <CssBaseline /> 
      <CustomScrollbarStyles />

      {/* Top AppBar */}
      <AppBar 
        position="absolute" 
        open={open}
        sx={{ backgroundColor: `${theme.palette.secondary.main}` }}>

        {/* keep right padding when drawer closed  */}
        <Toolbar sx={{ pr: '24px', }}> 

          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: '36px',
              ...(open && { display: 'none' }),
            }}>
            <MenuIcon />
          </IconButton>

          <img src={logo} alt="HCRC logo" height={60} style={{ paddingRight: 20 }}/>
          
          {/* <Typography
            component="h1"
            variant="h5"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1, fontWeight: 100}}>
            Data Portal
          </Typography> */}
          <Typography
            component="h1"

            color="inherit"
            noWrap
            sx={{ 
              flexGrow: 1,
              fontFamily: '"Gilroy", sans-serif',
              fontWeight: 100,
              letterSpacing: '0.03em',
              fontSize: '1.5rem',
              paddingLeft: 2,
              borderLeft: '3px solid rgba(255,255,255,0.4)',
              display: 'flex',
              alignItems: 'center',

            }}>
            DataConnect
          </Typography>

          {/* <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}/>            
          </Search> */}
          {/* {user.email} */}
          <UserMenu/>
        </Toolbar>

      </AppBar>

      {/* Left Drawer Menu */}
      <Drawer variant="permanent" open={open}>

        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: [1],
          }}>
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>

        <Divider />

        <List component="nav">
          {mainListItems}
          <Divider sx={{ my: 1 }} />
          {secondaryListItems}
        </List>

      </Drawer>

      {/* Content area */}
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? `${theme.palette.background.default}`
              : theme.palette.grey[900],
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh', 
          overflowY: 'hidden'
        }}>
        <Toolbar/> 
        <Container 
          maxWidth="lg" 
          sx={{ 
            flexGrow: 1,
            pt: 4, mb: 0, pb: 0,
            overflow: 'hidden' }}>
          {outlet}          
        </Container>
        <Copyright />
      </Box>
    </Box>
  );
}


// minHeight: 'calc(100vh - 140px)',
// 'calc(100vh - 140px)'

// const Search = styled('div')(({ theme }) => ({
//   position: 'relative',
//   borderRadius: theme.shape.borderRadius,
//   backgroundColor: alpha(theme.palette.common.white, 0.15),
//   '&:hover': {
//     backgroundColor: alpha(theme.palette.common.white, 0.25),
//   },
//   marginRight: theme.spacing(2),
//   marginLeft: 0,
//   width: '100%',
//   [theme.breakpoints.up('sm')]: {
//     marginLeft: theme.spacing(3),
//     width: 'auto',
//   },
// }));

// const SearchIconWrapper = styled('div')(({ theme }) => ({
//   padding: theme.spacing(0, 2),
//   height: '100%',
//   position: 'absolute',
//   pointerEvents: 'none',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
// }));

// const StyledInputBase = styled(InputBase)(({ theme }) => ({
//   color: 'inherit',
//   '& .MuiInputBase-input': {
//     padding: theme.spacing(1, 1, 1, 0),
//     // vertical padding + font size from searchIcon
//     paddingLeft: `calc(1em + ${theme.spacing(4)})`,
//     transition: theme.transitions.create('width'),
//     width: '100%',
//     [theme.breakpoints.up('md')]: {
//       width: '20ch',
//     },
//   },
// }));